/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Alert,
  Dimensions,
  Animated,
  FlatList,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';

const { width } = Dimensions.get('window');

const AchievementsScreen = () => {
  const navigation = useNavigation();
  const [userData, setUserData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));
  const [scaleAnim] = useState(new Animated.Value(0.8));

  // Enhanced gradient color schemes
  const gradients = {
    primary: ['#667eea', '#764ba2'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
    purple: ['#d299c2', '#fef9d7'],
    orange: ['#fdbb2d', '#22c1c3'],
    blue: ['#2196f3', '#21cbf3'],
    green: ['#56ab2f', '#a8e6cf'],
    pink: ['#ff9a9e', '#fecfef'],
    teal: ['#667eea', '#764ba2'],
    gold: ['#f7971e', '#ffd200'],
    silver: ['#c0c0c0', '#e8e8e8'],
    bronze: ['#cd7f32', '#d4af37'],
  };

  useEffect(() => {
    loadUserData();
    startAnimations();
  }, []);

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 1200,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const loadUserData = async () => {
    try {
      const storedUser = await AsyncStorage.getItem('user');
      const storedUserData = await AsyncStorage.getItem('userData');
      
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUserData(parsedUser);
      } else if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        setUserData(parsedUserData);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const getUserName = () => {
    if (!userData) return 'Student';
    
    const fullName = userData.full_name || userData.fullName || userData.name;
    if (fullName) return fullName;
    
    const firstName = userData.first_name || userData.firstName || '';
    const lastName = userData.last_name || userData.lastName || '';
    const combinedName = `${firstName} ${lastName}`.trim();
    
    return combinedName || 'Student';
  };

  const categories = ['All', 'Courses', 'Certificates', 'Skills', 'Streaks', 'Milestones'];

  // Comprehensive achievements data integrated with learning system
  const achievements = [
    // Course Completion Achievements
    {
      id: '1',
      title: 'First Steps',
      description: 'Complete your first engineering course',
      category: 'Courses',
      type: 'course_completion',
      icon: 'school',
      gradient: gradients.success,
      progress: 100,
      maxProgress: 1,
      isUnlocked: true,
      dateEarned: '2024-01-15',
      points: 50,
      rarity: 'common',
      relatedScreen: 'LearningCourses',
    },
    {
      id: '2',
      title: 'Course Conqueror',
      description: 'Complete 5 engineering courses',
      category: 'Courses',
      type: 'course_completion',
      icon: 'trophy',
      gradient: gradients.orange,
      progress: 3,
      maxProgress: 5,
      isUnlocked: false,
      points: 200,
      rarity: 'uncommon',
      relatedScreen: 'LearningCourses',
    },
    {
      id: '3',
      title: 'Engineering Master',
      description: 'Complete 20 engineering courses',
      category: 'Courses',
      type: 'course_completion',
      icon: 'crown',
      gradient: gradients.gold,
      progress: 8,
      maxProgress: 20,
      isUnlocked: false,
      points: 1000,
      rarity: 'legendary',
      relatedScreen: 'LearningCourses',
    },

    // Certificate Achievements
    {
      id: '4',
      title: 'Certified Professional',
      description: 'Earn your first professional certificate',
      category: 'Certificates',
      type: 'certificate',
      icon: 'certificate',
      gradient: gradients.purple,
      progress: 100,
      maxProgress: 1,
      isUnlocked: true,
      dateEarned: '2024-02-10',
      points: 100,
      rarity: 'uncommon',
      relatedScreen: 'LearningCertificateScreen',
    },
    {
      id: '5',
      title: 'Certificate Collector',
      description: 'Earn 5 professional certificates',
      category: 'Certificates',
      type: 'certificate',
      icon: 'medal',
      gradient: gradients.blue,
      progress: 2,
      maxProgress: 5,
      isUnlocked: false,
      points: 500,
      rarity: 'rare',
      relatedScreen: 'LearningCertificateScreen',
    },

    // Skill Development Achievements
    {
      id: '6',
      title: 'Skill Builder',
      description: 'Master your first technical skill',
      category: 'Skills',
      type: 'skill_mastery',
      icon: 'cog',
      gradient: gradients.warning,
      progress: 100,
      maxProgress: 1,
      isUnlocked: true,
      dateEarned: '2024-01-20',
      points: 75,
      rarity: 'common',
      relatedScreen: 'SkillsUpgradeCourses',
    },
    {
      id: '7',
      title: 'Multi-Skilled Engineer',
      description: 'Master 10 different technical skills',
      category: 'Skills',
      type: 'skill_mastery',
      icon: 'tools',
      gradient: gradients.teal,
      progress: 4,
      maxProgress: 10,
      isUnlocked: false,
      points: 750,
      rarity: 'rare',
      relatedScreen: 'SkillsUpgradeCourses',
    },

    // Learning Streak Achievements
    {
      id: '8',
      title: 'Consistent Learner',
      description: 'Maintain a 7-day learning streak',
      category: 'Streaks',
      type: 'streak',
      icon: 'fire',
      gradient: gradients.danger,
      progress: 100,
      maxProgress: 7,
      isUnlocked: true,
      dateEarned: '2024-02-01',
      points: 150,
      rarity: 'uncommon',
      relatedScreen: 'LearningDashboard',
    },
    {
      id: '9',
      title: 'Dedication Champion',
      description: 'Maintain a 30-day learning streak',
      category: 'Streaks',
      type: 'streak',
      icon: 'flame',
      gradient: gradients.orange,
      progress: 15,
      maxProgress: 30,
      isUnlocked: false,
      points: 500,
      rarity: 'rare',
      relatedScreen: 'LearningDashboard',
    },

    // Milestone Achievements
    {
      id: '10',
      title: 'Time Investor',
      description: 'Spend 100 hours learning',
      category: 'Milestones',
      type: 'time_milestone',
      icon: 'clock',
      gradient: gradients.info,
      progress: 240,
      maxProgress: 100,
      isUnlocked: true,
      dateEarned: '2024-02-15',
      points: 300,
      rarity: 'uncommon',
      relatedScreen: 'MyLearningHistoryScreen',
    },
    {
      id: '11',
      title: 'Knowledge Seeker',
      description: 'Complete 50 learning modules',
      category: 'Milestones',
      type: 'module_completion',
      icon: 'book-open-variant',
      gradient: gradients.green,
      progress: 32,
      maxProgress: 50,
      isUnlocked: false,
      points: 400,
      rarity: 'rare',
      relatedScreen: 'LearningCoursesScreen',
    },

    // Special Achievements
    {
      id: '12',
      title: 'Perfect Score',
      description: 'Score 100% on 5 course assessments',
      category: 'Milestones',
      type: 'perfect_score',
      icon: 'star',
      gradient: gradients.gold,
      progress: 2,
      maxProgress: 5,
      isUnlocked: false,
      points: 600,
      rarity: 'epic',
      relatedScreen: 'QuizScreen',
    },
    {
      id: '13',
      title: 'Virtual Intern',
      description: 'Complete your first virtual internship',
      category: 'Milestones',
      type: 'internship',
      icon: 'briefcase',
      gradient: gradients.secondary,
      progress: 0,
      maxProgress: 1,
      isUnlocked: false,
      points: 800,
      rarity: 'epic',
      relatedScreen: 'VirtualInternshipScreen',
    },
    {
      id: '14',
      title: 'Community Helper',
      description: 'Help 10 fellow students in discussions',
      category: 'Milestones',
      type: 'community',
      icon: 'account-group',
      gradient: gradients.pink,
      progress: 3,
      maxProgress: 10,
      isUnlocked: false,
      points: 250,
      rarity: 'uncommon',
      relatedScreen: 'MoreScreen',
    },
    {
      id: '15',
      title: 'Early Bird',
      description: 'Complete lessons before 8 AM for 10 days',
      category: 'Streaks',
      type: 'early_learning',
      icon: 'weather-sunny',
      gradient: gradients.warning,
      progress: 4,
      maxProgress: 10,
      isUnlocked: false,
      points: 200,
      rarity: 'uncommon',
      relatedScreen: 'LearningDashboard',
    },
  ];

  const filteredAchievements = achievements.filter(achievement => 
    selectedCategory === 'All' || achievement.category === selectedCategory
  );

  const unlockedAchievements = achievements.filter(a => a.isUnlocked);
  const totalPoints = unlockedAchievements.reduce((sum, a) => sum + a.points, 0);
  const completionPercentage = Math.round((unlockedAchievements.length / achievements.length) * 100);

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common': return gradients.success;
      case 'uncommon': return gradients.blue;
      case 'rare': return gradients.purple;
      case 'epic': return gradients.orange;
      case 'legendary': return gradients.gold;
      default: return gradients.primary;
    }
  };

  const handleAchievementPress = (achievement) => {
    if (achievement.relatedScreen) {
      Alert.alert(
        `🏆 ${achievement.title}`,
        `${achievement.description}\n\nWould you like to continue working towards this achievement?`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: '🚀 Continue Learning',
            onPress: () => navigation.navigate(achievement.relatedScreen),
          },
        ]
      );
    } else {
      Alert.alert(
        `🏆 ${achievement.title}`,
        achievement.description,
        [{ text: 'OK' }]
      );
    }
  };

  const renderStatsSection = () => (
    <Animated.View 
      style={[
        styles.statsContainer,
        {
          opacity: fadeAnim,
          transform: [{ scale: scaleAnim }],
        },
      ]}
    >
      <LinearGradient
        colors={gradients.primary}
        style={styles.statsGradient}
      >
        <View style={styles.statItem}>
          <MaterialCommunityIcons name="trophy" size={32} color="#ffffff" />
          <Text style={styles.statNumber}>{unlockedAchievements.length}</Text>
          <Text style={styles.statLabel}>🏆 Unlocked</Text>
        </View>
        <View style={styles.statDivider} />
        <View style={styles.statItem}>
          <MaterialCommunityIcons name="star" size={32} color="#ffffff" />
          <Text style={styles.statNumber}>{totalPoints.toLocaleString()}</Text>
          <Text style={styles.statLabel}>⭐ Points</Text>
        </View>
        <View style={styles.statDivider} />
        <View style={styles.statItem}>
          <MaterialCommunityIcons name="chart-line" size={32} color="#ffffff" />
          <Text style={styles.statNumber}>{completionPercentage}%</Text>
          <Text style={styles.statLabel}>📈 Complete</Text>
        </View>
      </LinearGradient>
    </Animated.View>
  );

  const renderProgressSection = () => (
    <Animated.View 
      style={[
        styles.progressContainer,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <Text style={styles.sectionTitle}>📊 Overall Progress</Text>
      <View style={styles.progressBarContainer}>
        <LinearGradient
          colors={gradients.success}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={[styles.progressFill, { width: `${completionPercentage}%` }]}
        />
      </View>
      <Text style={styles.progressText}>
        🎯 {unlockedAchievements.length} of {achievements.length} achievements unlocked
      </Text>
    </Animated.View>
  );

  const renderCategoryTabs = () => (
    <Animated.View 
      style={[
        styles.categoryContainer,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryScroll}>
        {categories.map((category) => (
          <TouchableOpacity
            key={category}
            style={styles.categoryTab}
            onPress={() => setSelectedCategory(category)}
          >
            {selectedCategory === category ? (
              <LinearGradient
                colors={gradients.primary}
                style={styles.categoryTabGradient}
              >
                <Text style={styles.activeCategoryText}>{category}</Text>
              </LinearGradient>
            ) : (
              <Text style={styles.categoryText}>{category}</Text>
            )}
          </TouchableOpacity>
        ))}
      </ScrollView>
    </Animated.View>
  );

  const renderAchievementCard = ({ item }) => (
    <Animated.View
      style={[
        styles.achievementCard,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <TouchableOpacity onPress={() => handleAchievementPress(item)}>
        <View style={styles.cardHeader}>
          <LinearGradient
            colors={item.isUnlocked ? item.gradient : ['#e5e7eb', '#f3f4f6']}
            style={styles.achievementIconGradient}
          >
            <MaterialCommunityIcons 
              name={item.icon} 
              size={32} 
              color={item.isUnlocked ? "#ffffff" : "#9ca3af"} 
            />
          </LinearGradient>
          <View style={styles.achievementInfo}>
            <View style={styles.titleRow}>
              <Text style={[
                styles.achievementTitle,
                !item.isUnlocked && styles.lockedTitle
              ]}>
                {item.title}
              </Text>
              <LinearGradient
                colors={getRarityColor(item.rarity)}
                style={styles.rarityBadge}
              >
                <Text style={styles.rarityText}>{String(item.rarity ?? '').toUpperCase()}</Text>
              </LinearGradient>
            </View>
            <Text style={[
              styles.achievementDescription,
              !item.isUnlocked && styles.lockedDescription
            ]}>
              {item.description}
            </Text>
            {item.isUnlocked && item.dateEarned && (
              <Text style={styles.dateEarned}>
                🗓️ Earned on {new Date(item.dateEarned).toLocaleDateString()}
              </Text>
            )}
          </View>
        </View>

        {!item.isUnlocked && (
          <View style={styles.progressSection}>
            <View style={styles.progressHeader}>
              <Text style={styles.progressLabel}>Progress</Text>
              <Text style={styles.progressNumbers}>
                {item.progress}/{item.maxProgress}
              </Text>
            </View>
            <View style={styles.progressBarContainer}>
              <LinearGradient
                colors={item.gradient}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={[
                  styles.progressFill, 
                  { width: `${Math.min((item.progress / item.maxProgress) * 100, 100)}%` }
                ]}
              />
            </View>
          </View>
        )}

        <View style={styles.cardFooter}>
          <View style={styles.pointsContainer}>
            <LinearGradient
              colors={gradients.gold}
              style={styles.pointsGradient}
            >
              <MaterialCommunityIcons name="star" size={16} color="#ffffff" />
              <Text style={styles.pointsText}>{item.points} pts</Text>
            </LinearGradient>
          </View>
          {item.isUnlocked ? (
            <LinearGradient
              colors={gradients.success}
              style={styles.statusGradient}
            >
              <MaterialCommunityIcons name="check-circle" size={16} color="#ffffff" />
              <Text style={styles.statusText}>Unlocked</Text>
            </LinearGradient>
          ) : (
            <LinearGradient
              colors={gradients.info}
              style={styles.statusGradient}
            >
              <MaterialCommunityIcons name="lock" size={16} color="#ffffff" />
              <Text style={styles.statusText}>Locked</Text>
            </LinearGradient>
          )}
        </View>
      </TouchableOpacity>
    </Animated.View>
  );

  const renderRecentAchievements = () => {
    const recentAchievements = unlockedAchievements
      .filter(a => a.dateEarned)
      .sort((a, b) => new Date(b.dateEarned) - new Date(a.dateEarned))
      .slice(0, 3);

    if (recentAchievements.length === 0) return null;

    return (
      <Animated.View 
        style={[
          styles.recentContainer,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <Text style={styles.sectionTitle}>🎉 Recent Achievements</Text>
        {recentAchievements.map((achievement) => (
          <TouchableOpacity
            key={achievement.id}
            style={styles.recentItem}
            onPress={() => handleAchievementPress(achievement)}
          >
            <LinearGradient
              colors={achievement.gradient}
              style={styles.recentIconGradient}
            >
              <MaterialCommunityIcons name={achievement.icon} size={20} color="#ffffff" />
            </LinearGradient>
            <View style={styles.recentInfo}>
              <Text style={styles.recentTitle}>{achievement.title}</Text>
              <Text style={styles.recentDate}>
                {new Date(achievement.dateEarned).toLocaleDateString()}
              </Text>
            </View>
            <LinearGradient
              colors={gradients.gold}
              style={styles.recentPointsGradient}
            >
              <Text style={styles.recentPoints}>+{achievement.points}</Text>
            </LinearGradient>
          </TouchableOpacity>
        ))}
      </Animated.View>
    );
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />
      
      {/* Enhanced Header */}
      <LinearGradient
        colors={gradients.primary}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={styles.header}
      >
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={28} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>🏆 Achievements</Text>
        <TouchableOpacity
          onPress={() => navigation.navigate('LearningDashboard')}
          style={styles.dashboardButton}
        >
          <MaterialCommunityIcons name="view-dashboard" size={28} color="#ffffff" />
        </TouchableOpacity>
      </LinearGradient>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {/* Welcome Section */}
        <Animated.View 
          style={[
            styles.welcomeSection,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          <LinearGradient
            colors={gradients.orange}
            style={styles.welcomeGradient}
          >
            <Text style={styles.welcomeTitle}>🎯 Welcome, {getUserName()}!</Text>
            <Text style={styles.welcomeSubtitle}>
              ✨ Track your learning journey and celebrate your achievements
            </Text>
          </LinearGradient>
        </Animated.View>

        {renderStatsSection()}
        {renderProgressSection()}
        {renderRecentAchievements()}
        {renderCategoryTabs()}

        {/* Achievements List */}
        <View style={styles.achievementsContainer}>
          <FlatList
            data={filteredAchievements}
            renderItem={renderAchievementCard}
            keyExtractor={item => item.id}
            scrollEnabled={false}
            showsVerticalScrollIndicator={false}
          />
        </View>

        {/* Quick Actions */}
        <Animated.View 
          style={[
            styles.quickActionsContainer,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          <Text style={styles.sectionTitle}>🚀 Continue Learning</Text>
          <View style={styles.actionsGrid}>
            <TouchableOpacity
              style={styles.actionCard}
              onPress={() => navigation.navigate('LearningCourses')}
            >
              <LinearGradient
                colors={gradients.blue}
                style={styles.actionGradient}
              >
                <MaterialCommunityIcons name="book-open-variant" size={24} color="#ffffff" />
                <Text style={styles.actionText}>📚 Courses</Text>
              </LinearGradient>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.actionCard}
              onPress={() => navigation.navigate('VirtualInternshipScreen')}
            >
              <LinearGradient
                colors={gradients.success}
                style={styles.actionGradient}
              >
                <MaterialCommunityIcons name="briefcase" size={24} color="#ffffff" />
                <Text style={styles.actionText}>💼 Internships</Text>
              </LinearGradient>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.actionCard}
              onPress={() => navigation.navigate('SkillsUpgradeCourses')}
            >
              <LinearGradient
                colors={gradients.warning}
                style={styles.actionGradient}
              >
                <MaterialCommunityIcons name="trending-up" size={24} color="#ffffff" />
                <Text style={styles.actionText}>📈 Skills</Text>
              </LinearGradient>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.actionCard}
              onPress={() => navigation.navigate('LearningCertificateScreen')}
            >
              <LinearGradient
                colors={gradients.purple}
                style={styles.actionGradient}
              >
                <MaterialCommunityIcons name="certificate" size={24} color="#ffffff" />
                <Text style={styles.actionText}>🎓 Certificates</Text>
              </LinearGradient>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  dashboardButton: {
    padding: 8,
  },
  container: {
    flex: 1,
  },
  welcomeSection: {
    margin: 20,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  welcomeGradient: {
    padding: 24,
    alignItems: 'center',
  },
  welcomeTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  welcomeSubtitle: {
    fontSize: 16,
    color: '#ffffff',
    opacity: 0.9,
    textAlign: 'center',
  },
  statsContainer: {
    margin: 20,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  statsGradient: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: 24,
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statDivider: {
    width: 1,
    height: 40,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    marginHorizontal: 16,
  },
  statNumber: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    marginTop: 8,
    marginBottom: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  statLabel: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '600',
    textAlign: 'center',
    opacity: 0.9,
  },
  progressContainer: {
    backgroundColor: '#ffffff',
    margin: 20,
    padding: 24,
    borderRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 16,
  },
  progressBarContainer: {
    height: 12,
    backgroundColor: '#e5e7eb',
    borderRadius: 6,
    marginBottom: 12,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 6,
  },
  progressText: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
  },
  recentContainer: {
    backgroundColor: '#ffffff',
    margin: 20,
    padding: 24,
    borderRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  recentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  recentIconGradient: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  recentInfo: {
    flex: 1,
  },
  recentTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: 4,
  },
  recentDate: {
    fontSize: 12,
    color: '#6b7280',
  },
  recentPointsGradient: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  recentPoints: {
    fontSize: 12,
    fontWeight: '700',
    color: '#ffffff',
  },
  categoryContainer: {
    backgroundColor: '#ffffff',
    marginHorizontal: 20,
    marginBottom: 20,
    padding: 16,
    borderRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  categoryScroll: {
    flexDirection: 'row',
  },
  categoryTab: {
    marginRight: 12,
    borderRadius: 20,
    overflow: 'hidden',
  },
  categoryTabGradient: {
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
  categoryText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6b7280',
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: '#f8fafc',
    borderRadius: 20,
  },
  activeCategoryText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#ffffff',
  },
  achievementsContainer: {
    paddingHorizontal: 20,
  },
  achievementCard: {
    backgroundColor: '#ffffff',
    borderRadius: 20,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  cardHeader: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  achievementIconGradient: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  achievementInfo: {
    flex: 1,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  achievementTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
    flex: 1,
    marginRight: 12,
  },
  lockedTitle: {
    color: '#9ca3af',
  },
  rarityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  rarityText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#ffffff',
  },
  achievementDescription: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
    marginBottom: 8,
  },
  lockedDescription: {
    color: '#9ca3af',
  },
  dateEarned: {
    fontSize: 12,
    color: '#10b981',
    fontWeight: '500',
  },
  progressSection: {
    marginBottom: 16,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  progressNumbers: {
    fontSize: 14,
    fontWeight: '700',
    color: '#6b7280',
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  pointsContainer: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  pointsGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  pointsText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#ffffff',
    marginLeft: 4,
  },
  statusGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#ffffff',
    marginLeft: 4,
  },
  quickActionsContainer: {
    backgroundColor: '#ffffff',
    margin: 20,
    padding: 24,
    borderRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  actionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  actionCard: {
    width: '48%',
    marginBottom: 16,
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  actionGradient: {
    alignItems: 'center',
    padding: 20,
  },
  actionText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#ffffff',
    marginTop: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
});

export default AchievementsScreen;

